import * as React from "react";
import { CheckIcon, XIcon } from "lucide-react";
import { cn } from "~/lib/utils";

// // // //

/**
 * DrawerBackdrop
 * Full-screen gradient overlay rendered behind a bottom drawer.
 */
function DrawerBackdrop({
    className,
    blur = false,
    ...props
}: React.ComponentProps<"div"> & {
    blur?: boolean;
}) {
    return (
        <div
            data-slot="drawer-backdrop"
            className={cn(
                "fixed inset-0 bg-linear-to-t from-slate-900/90 to-slate-900/30 z-100 transition-all duration-300",
                { "backdrop-blur-xs": blur },
                className,
            )}
            {...props}
        />
    );
}

/**
 * DrawerCloseButton
 * Absolutely positioned "X" button for the top-right corner of a `DrawerSheet`.
 */
function DrawerCloseButton({
    className,
    ...props
}: React.ComponentProps<"button">) {
    return (
        <button
            type="button"
            data-slot="drawer-close-button"
            aria-label="Close"
            className={cn(
                "absolute top-3 right-3 p-1.5 text-muted-foreground hover:text-foreground transition-colors",
                className,
            )}
            {...props}
        >
            <XIcon className="w-4 h-4" />
        </button>
    );
}

/**
 * DrawerSheet
 * Bottom-anchored sliding panel. Pass `offsetLeft` to clear a sidebar on
 * large screens.
 */
function DrawerSheet({
    className,
    style,
    offsetLeft = 0,
    children,
    ...props
}: React.ComponentProps<"div"> & {
    offsetLeft?: number;
}) {
    return (
        <div
            data-slot="drawer-sheet"
            className={cn(
                "fixed bottom-0 right-0 z-150 transform transition-all duration-300 ease-out animate-in slide-in-from-bottom select-none",
                className,
            )}
            style={{ left: offsetLeft, ...style }}
            {...props}
        >
            <div className="bg-card">{children}</div>
        </div>
    );
}

/**
 * DrawerContent
 * Centered, max-width content container for the body of a `DrawerSheet`.
 */
function DrawerContent({ className, ...props }: React.ComponentProps<"div">) {
    return (
        <div
            data-slot="drawer-content"
            className={cn(
                "max-w-4xl mx-auto px-5 py-16 text-center",
                className,
            )}
            {...props}
        />
    );
}

/**
 * DrawerHeader
 * Headline for a drawer.
 */
function DrawerHeader({ className, ...props }: React.ComponentProps<"h2">) {
    return (
        <h2
            data-slot="drawer-header"
            className={cn(
                "text-2xl md:text-3xl font-bold text-foreground mb-3 text-balance",
                className,
            )}
            {...props}
        />
    );
}

/**
 * DrawerSubheader
 * Supporting paragraph beneath a `DrawerHeader`.
 */
function DrawerSubheader({ className, ...props }: React.ComponentProps<"p">) {
    return (
        <p
            data-slot="drawer-subheader"
            className={cn(
                "text-muted-foreground mb-6 text-lg max-w-lg mx-auto",
                className,
            )}
            {...props}
        />
    );
}

/**
 * DrawerFooter
 * Wrapping row of `DrawerFooterItem`s, separated from the content by a border.
 */
function DrawerFooter({ className, ...props }: React.ComponentProps<"div">) {
    return (
        <div
            data-slot="drawer-footer"
            className={cn(
                "flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 mt-6 pt-5 border-t text-sm text-muted-foreground font-sans",
                className,
            )}
            {...props}
        />
    );
}

/**
 * DrawerFooterItem
 * Single entry in a `DrawerFooter`, optionally prefixed with a checkmark.
 */
function DrawerFooterItem({
    className,
    showCheckmark = false,
    children,
    ...props
}: React.ComponentProps<"span"> & {
    showCheckmark?: boolean;
}) {
    return (
        <span
            data-slot="drawer-footer-item"
            className={cn("flex items-center gap-1.5", className)}
            {...props}
        >
            {showCheckmark && (
                <CheckIcon className="w-3.5 h-3.5 text-green-600" />
            )}
            {children}
        </span>
    );
}

export {
    DrawerBackdrop,
    DrawerCloseButton,
    DrawerContent,
    DrawerFooter,
    DrawerFooterItem,
    DrawerHeader,
    DrawerSheet,
    DrawerSubheader,
};
